import { NgModule, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { ClipboardModule } from 'ngx-clipboard';
import { TranslateModule } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { NgbDatepickerI18n, NgbInputDatepickerConfig, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { environment } from 'src/environments/environment';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { MatChipsModule } from '@angular/material/chips';
// #fake-start#
import { FakeAPIService } from './_fake/fake-api.service';
import { registerLocaleData } from '@angular/common';
// #fake-end#
import localeEs from '@angular/common/locales/es';
import localeCa from '@angular/common/locales/ca';
import localeEnGb from '@angular/common/locales/en-GB';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthInterceptor, BO_API_CONFIG, BoPermissionsService, ErrorInterceptor } from '@bo/core';
import { BO_AUTH_CONFIG, BoAuthLanding, BoAuthService } from '@bo/auth';
import { WorkSchemaInterceptor } from './core/interceptors/work-schema.interceptor';
import { PermissionsService } from './core/menu/permissions.service';
import { AuthService } from './modules/auth/services/auth.service';
import { LandingRouteService } from './services/landing-route.service';
import { ForgetReadsInterceptor } from './core/interceptors/forget-reads.interceptor';
import { DemoInterceptor } from './core/interceptors/demo.interceptor';
import { FleetMockInterceptor } from './mocks/drivers-fleet/fleet-mock.interceptor';
import { PartnerPaymentsMockInterceptor } from './mocks/partner-payments/partner-payments-mock.interceptor';
import { ZoneAdSettingsMockInterceptor } from './mocks/zone-ad-settings/zone-ad-settings-mock.interceptor';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

import { AgChartsModule } from 'ag-charts-angular';
import { LicenseManager } from 'ag-charts-enterprise';
LicenseManager.setLicenseKey(
  'Using_this_{AG_Charts}_Enterprise_key_{AG-103678}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{WAIIS_SOLUTIONS_IBERIA_SL}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{WAIIS_Dashboard}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{WAIIS_Dashboard}_need_to_be_licensed___{WAIIS_Dashboard}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts}_Enterprise_versions_released_before_{18_October_2026}____[v3]_[02]_MTc5MjI3ODAwMDAwMA==bab204f1cf435643611d789d0f9f80cb',
);

// Calendario de ng-bootstrap en el idioma de la interfaz (es / ca / en):
// nombres de mes y día con Intl y el locale de utils/locale.ts (WS-2274).
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { angularLocaleFor, dateLocaleFor } from './utils/locale';
import { TranslationService } from './modules/i18n/translation.service';

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {
  constructor(private translate: TranslateService) {
    super();
  }

  private get locale(): string {
    return dateLocaleFor(this.translate.currentLang);
  }

  /** 1 = lunes … 7 = domingo (ISO), como numera ng-bootstrap. 2024-01-01 fue lunes. */
  private weekdayDate(weekday: number): Date {
    return new Date(2024, 0, weekday);
  }

  getWeekdayLabel(weekday: number): string {
    const name = new Intl.DateTimeFormat(this.locale, { weekday: 'short' }).format(this.weekdayDate(weekday));
    return capitalize(name.replace('.', ''));
  }
  getWeekdayShortName(weekday: number): string {
    return this.getWeekdayLabel(weekday);
  }
  getMonthShortName(month: number): string {
    const name = new Intl.DateTimeFormat(this.locale, { month: 'short' }).format(new Date(2024, month - 1, 1));
    return capitalize(name.replace('.', ''));
  }
  getMonthFullName(month: number): string {
    // En catalán Intl da «de gener» / «d’abril»: se quita la preposición.
    const name = new Intl.DateTimeFormat(this.locale, { month: 'long' }).format(new Date(2024, month - 1, 1));
    return capitalize(name.replace(/^(de |d[’'])/, ''));
  }

  getDayAriaLabel(date: { year: number; month: number; day: number }): string {
    return new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(
      new Date(date.year, date.month - 1, date.day),
    );
  }
}

registerLocaleData(localeEs);
// LOCALE_ID (ver providers) y `| langDate` (shared/pipes/lang-date.pipe.ts): formatos del idioma de la interfaz.
registerLocaleData(localeCa);
registerLocaleData(localeEnGb);

// AoT requires an exported function for factories

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot(),
    HttpClientModule,
    ClipboardModule,
    // #fake-start#
    environment.isMockEnabled
      ? HttpClientInMemoryWebApiModule.forRoot(FakeAPIService, {
          passThruUnknownUrl: true,
          dataEncapsulation: false,
        })
      : [],
    // #fake-end#
    AppRoutingModule,
    InlineSVGModule.forRoot(),
    NgbModule,
    SweetAlert2Module.forRoot(),
    MatChipsModule,
    AgChartsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  providers: [
    // LOCALE_ID sigue al idioma elegido al arrancar (es → 'es', ca → 'ca',
    // en → 'en-GB'): `| date`, `| number`, `| currency`, `| percent` y el
    // datepicker de Material salen con el formato del idioma. Como LOCALE_ID
    // no cambia en caliente, el selector de idioma recarga la página.
    {
      provide: LOCALE_ID,
      useFactory: (translation: TranslationService) => angularLocaleFor(translation.getSelectedLanguage()),
      deps: [TranslationService],
    },
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
    // El primero: en el dashboard de ejemplo (/demo) ninguna petición llega al
    // back, ni pasa por el token ni por los avisos de error (#20261023-090014).
    { provide: HTTP_INTERCEPTORS, useClass: DemoInterceptor, multi: true },
    // URLs del API para el código compartido (@bo/core).
    {
      provide: BO_API_CONFIG,
      useValue: { laravelApi: environment.laravelApi, laravelBackoffice: environment.laravelBackoffice },
    },
    // Una sola instancia de permisos: el código compartido pide
    // BoPermissionsService y el dashboard, PermissionsService (que lo extiende).
    { provide: BoPermissionsService, useExisting: PermissionsService },
    // Acceso (@bo/auth): origen de los emails de contraseña y salida al cerrar sesión.
    {
      provide: BO_AUTH_CONFIG,
      useValue: {
        origin: 'dashboard',
        signup: true,
        logoutRoute: ['/auth/welcome'],
        homeRoute: ['/dashboard'],
        trackWelcomeVisits: environment.environmentName === 'production',
      },
    },
    // Adónde va el usuario al entrar: invitación, partner, registro a medias o tablero.
    { provide: BoAuthLanding, useExisting: LandingRouteService },
    // Una sola sesión: el código compartido pide BoAuthService y el dashboard,
    // AuthService (que lo extiende).
    { provide: BoAuthService, useExisting: AuthService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    // Antes que AuthInterceptor: reescribe la URL al esquema `work` (solo dashboard).
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WorkSchemaInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    // Tras escribir, las lecturas guardadas durante la navegación ya no valen.
    { provide: HTTP_INTERCEPTORS, useClass: ForgetReadsInterceptor, multi: true },
    // El último: los errores simulados pasan por ErrorInterceptor como los reales.
    ...(environment.fleetMock
      ? [{ provide: HTTP_INTERCEPTORS, useClass: FleetMockInterceptor, multi: true }]
      : []),
    // «Condiciones de pago» del partner, simulado mientras no hay backend.
    ...(environment.partnerPaymentsMock
      ? [{ provide: HTTP_INTERCEPTORS, useClass: PartnerPaymentsMockInterceptor, multi: true }]
      : []),
    // Ajustes de anuncios por zona, simulados mientras no hay backend.
    ...(environment.zoneAdSettingsMock
      ? [{ provide: HTTP_INTERCEPTORS, useClass: ZoneAdSettingsMockInterceptor, multi: true }]
      : []),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(datepickerConfig: NgbInputDatepickerConfig) {
    // Calendario de ng-bootstrap (ngbDatepicker) en todo el admin: mes y año
    // como título con flechas, en vez de dos <select> nativos cuya lista pinta
    // el navegador; semana empezando en lunes. Solo aspecto: no cambia valores.
    // El componente app-bo-date-input pinta su propia cabecera (app-bo-select)
    // con navigation 'none'. Guía: «Campos de formulario › Calendario».
    datepickerConfig.navigation = 'arrows';
    datepickerConfig.firstDayOfWeek = 1;
  }
}
